import { Button } from 'components/common';
import useBottomSheet from 'hooks/common/useBottomSheet';
import { useEffect, useState } from 'react';

type SmartStoreOrderState =
  | { type: 'loading' }
  | { type: 'error'; message: string }
  | { type: 'confirm'; coinQuantity: number; productQuantity: number }
  | { type: 'success'; coinQuantity: number; productQuantity: number; currentCoin: number };

const SmartStoreOrder = () => {
  const { sheets, close } = useBottomSheet();
  const sheet = sheets.SmartStoreOrder;
  const { commonOrderId } = sheet.data;

  const [state, setState] = useState<SmartStoreOrderState>({ type: 'loading' });
  const [isProcessing, setIsProcessing] = useState(false);

  // 컴포넌트 마운트 시 주문 검증
  useEffect(() => {
    if (!commonOrderId) {
      setState({ type: 'error', message: '주문 정보가 없습니다.' });
      return;
    }

    (async () => {
      try {
        const orderInfo = await SmartStoreOrderDao.validateOrder(commonOrderId);
        ba('smart-store-order', undefined, {
          type: 'validate-success',
          fbUid: myinfo.fbUid,
          commonOrderId: commonOrderId,
          coinQuantity: orderInfo.coinQuantity,
          productQuantity: orderInfo.productQuantity,
        });
        setState({
          type: 'confirm',
          coinQuantity: orderInfo.coinQuantity,
          productQuantity: orderInfo.productQuantity,
        });
      } catch (err: any) {
        ba('smart-store-order', undefined, {
          type: 'validate-failed',
          fbUid: myinfo.fbUid,
          commonOrderId: commonOrderId,
          error: err?.error,
        });
        let errorMessage = '주문 조회 중 오류가 발생했습니다.';
        if (err.error === 'order-already-used') {
          errorMessage = '이미 사용된 링크입니다.';
        } else if (err.error === 'order-not-found' || err.error === 'app-mismatch') {
          errorMessage = '주문을 찾을 수 없습니다.';
        }
        setState({ type: 'error', message: errorMessage });
      }
    })();
  }, [commonOrderId]);

  // 충전 확인 버튼 클릭 시 코인 충전 처리
  const handleConfirm = async () => {
    if (isProcessing) return;

    setIsProcessing(true);

    try {
      const result = await SmartStoreOrderDao.redeemOrder(commonOrderId);
      ba('smart-store-order', undefined, {
        type: 'redeem-success',
        fbUid: myinfo.fbUid,
        commonOrderId: commonOrderId,
        coinQuantity: result.coinQuantity,
        productQuantity: result.productQuantity,
        currentCoin: result.currentCoin,
      });
      setState({
        type: 'success',
        coinQuantity: result.coinQuantity,
        productQuantity: result.productQuantity,
        currentCoin: result.currentCoin,
      });
    } catch (err: any) {
      ba('smart-store-order', undefined, {
        type: 'redeem-failed',
        fbUid: myinfo.fbUid,
        commonOrderId: commonOrderId,
        error: err?.error,
      });
      let errorMessage = '충전 처리 중 오류가 발생했습니다.';
      if (err.error === 'order-already-used') {
        errorMessage = '이미 사용된 링크입니다.';
      } else if (err.error === 'order-not-found' || err.error === 'app-mismatch') {
        errorMessage = '주문을 찾을 수 없습니다.';
      } else if (err.error === 'coin-add-failed') {
        errorMessage = '코인 충전에 실패했습니다. 잠시 후 다시 시도해주세요. 문제가 지속되면 고객센터로 문의해주세요.';
      }
      setState({ type: 'error', message: errorMessage });
    } finally {
      setIsProcessing(false);
    }
  };

  // 취소 버튼 클릭 시 바텀시트 닫기
  const handleClose = () => {
    close(sheet.id);
  };

  // Loading 상태
  if (state.type === 'loading') {
    return (
      <div className="flex h-full w-full flex-col items-center justify-center">
        <div className="typography-body-md text-base-txt-secondary">로딩 중...</div>
      </div>
    );
  }

  // Error 상태
  if (state.type === 'error') {
    return (
      <div className="flex h-full w-full flex-col items-center">
        <div className="typography-title-md text-dialog-txt-default">{state.message}</div>
        <div className="mt-6 w-full">
          <Button onClick={handleClose} style={{ text: '확인', color: 'btn-bg-primary', fontColor: 'btn-txt-primary' }} className="w-full" />
        </div>
      </div>
    );
  }

  // Success 상태
  if (state.type === 'success') {
    const totalCoinQuantity = state.coinQuantity * state.productQuantity;

    return (
      <div className="flex h-full w-full flex-col items-center">
        <div className="typography-title-md text-dialog-txt-default">{totalCoinQuantity} 코인이 충전되었습니다.</div>
        <div className="typography-body-sm mt-2 text-base-txt-secondary">
          코인 충전: {state.coinQuantity} 코인 x {state.productQuantity}개
        </div>
        <div className="typography-body-sm mt-1 text-base-txt-secondary">현재 보유 코인: {state.currentCoin}</div>
        <div className="mt-6 w-full">
          <Button onClick={handleClose} style={{ text: '확인', color: 'btn-bg-primary', fontColor: 'btn-txt-primary' }} className="w-full" />
        </div>
      </div>
    );
  }

  // Confirm 상태
  const nickname = myinfo?.nick || 'unknown';
  const totalCoinQuantity = state.coinQuantity * state.productQuantity;
  const currentCoin = MyDataManager.getCoin();
  const afterCoin = currentCoin + totalCoinQuantity;

  return (
    <div className="flex h-full w-full flex-col items-center">
      <div className="typography-title-md text-dialog-txt-default">{totalCoinQuantity} 코인을 충전하시겠습니까?</div>
      <div className="typography-body-sm mt-2 text-base-txt-secondary">
        코인 충전: {state.coinQuantity} 코인 x {state.productQuantity}개
      </div>
      <div className="mt-4 flex w-full flex-col gap-2 rounded-lg bg-base-bg-subtle p-4">
        <div className="typography-body-sm text-base-txt-secondary">계정: {nickname}</div>
        <div className="typography-body-sm text-base-txt-secondary">현재 코인: {currentCoin.toLocaleString()}</div>
        <div className="typography-body-sm text-base-txt-primary">충전 후 코인: {afterCoin.toLocaleString()}</div>
      </div>
      <div className="mt-6 flex w-full flex-row gap-1">
        <Button
          onClick={isProcessing ? undefined : handleClose}
          style={{ text: '취소', color: 'btn-bg-normal', fontColor: 'btn-txt-normal' }}
          className={`w-full ${isProcessing ? 'cursor-not-allowed opacity-50' : ''}`}
        />
        <Button
          onClick={isProcessing ? undefined : handleConfirm}
          style={{ text: isProcessing ? '처리 중...' : '충전하기', color: 'btn-bg-primary', fontColor: 'btn-txt-primary' }}
          className={`w-full ${isProcessing ? 'cursor-not-allowed opacity-50' : ''}`}
        />
      </div>
    </div>
  );
};

export default SmartStoreOrder;
